import { clone, difference, isEmpty, noop } from "lodash";
import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { withRouter, type WithRouterProps } from "adapters/withRouter/withRouter";
import { createAccountThunk } from "./state/thunks/createAccountThunk";
import Contacts from "./Contacts/Contacts";
import { type AddOn, type SavingAddOn } from "../types";
import { bindAction } from "interfaces";
import { type AppDispatch, type RootState } from "../../Application/globaltypes/redux";
import type NewContact from "../../../interfaces/NewContact";
import navigationUtils from "../../../utils/navigationUtils";
import { type INotifyByEmailController } from "components/notifyStep/notifyByEmailController";
import { withNotifyConfig, type WithNotifyConfigProps } from "../../SystemNotifications/containers/withNotifyConfig";
import { initialNotifyConfigDefault } from "../../SystemNotifications/config";
import { type NotifySettings } from "../../SystemNotifications/types";
import { DeleteLinkButton, NotifyWizardStep, PreventTransitionPrompt, RestrictedResource } from "../../../components";
import { NotifyStepSwitch } from "components/notifyStep/NotifyStepSwitch";
import AccessRestrictedMessage from "../../../components/restrictedRoute/AccessRestrictedMessage";
import { RolePermissions, RouteNames, TemplateTypes } from "../../../enums";
import Restricted from "../../Application/Restricted";
import dataService from "../../Application/services/dataServices/dataService";
import * as accountsEventTypes from "../../Application/services/realTimeNotification/events/accounts/accountsEvents";
import AddOnsConfiguration from "../EditAccount/AddOnsConfiguration/AddOnsConfiguration";
import { EditingAddOn } from "../EditAccount/EditAddOns/EditAddOns";
import CreateAccountPacksList from "./CreateAccountPacksList/CreateAccountPacksList";
import { type LDProps } from "features/LDProps";
import CreateAccountTemplates from "./Templates/CreateAccountTemplates";
import ConfirmTemplatesPacksModal from "../Templates/ConfirmTemplatesPacksModal";
import { type PackAccountsContextItem } from "components/assignmentModals/groupTemplatePacksConfirmationModal/types";
import { addTemplatesThunk, saveTemplatesPacks } from "../state/thunks/AccountTemplatesThunk";
import { getCreateSteps } from "./constants";
import { setSelected } from "../state/slices/availableTemplatesSlice";
import CreateAccountConfiguration from "features/Accounts/CreateAccount/CreateAccountConfiguration/CreateAccountConfiguration";
import { assignAddOns as assignAddOnsAction, fetchAddOns } from "features/Accounts/state/thunks/addOnsThunk";
import { resetAddOns as resetAddOnsAction } from "features/Accounts/state/slices/addOnsSlice";
import { resetAccountConfiguration as resetAccountConfigurationAction } from "features/Accounts/CreateAccount/state/slices/createAccountSlice";
import Wizard from "components/wizard/WizardNew";
import { Section } from "components/wizard";
import { getSelectedPacks } from "features/Accounts/state/selectors/availablePacksSelector";
import { setSelectedPacks } from "features/Accounts/state/slices/availablePacksSlice";
import { useRtn } from "hooks/useRtn";
import { reset as resetCustomers } from "../../People/GroupTemplate/state/TemplateAvailableCustomersSlice";
import { addDomainToAccount } from "../state/thunks/accountDomainsThunk";
import { reset as resetAvailableDomains } from "../state/slices/accountDomainsSlice";
import { selectHasTemplateUsersTabAccess, selectHasTemplatesTabAccess } from "../state/selectors/accountSelectors";
import { type TemplateUsersRef } from "../TemplateUsers/TemplateUsers";
import CreateAccountTemplateUsers from "./TemplateUsers/CreateAccountTemplateUsers";
import { useObservable } from "hooks/useObservable";
import { AddTemplateUserButton } from "../TemplateUsers/AddTemplateUserButton";
import {
  setSelected as setSelectedUsersAction,
  reset as resetTemplateUsers,
} from "features/Accounts/state/slices/editAccountUsersSlice";
import { isAccountTypeSelector } from "features/Application/selectors/userProfileSelectors";
import { AccountTypeEnum } from "interfaces/Account";

export type CreateAccountProps = PropsFromRedux &
  WithRouterProps &
  WithNotifyConfigProps<NotifySettings> & {
    prefilled?: {
      name: string;
      logoUri: Nullable<string>;
    };
    onRemoveName?: () => void;
  } & LDProps;

const packsRtn = [accountsEventTypes.AddAllPacksToOwnAccountSuccess, accountsEventTypes.AddAllPacksToOwnAccountFailure];

export const CreateAccount: React.FC<CreateAccountProps> = ({
  addOns,
  fetchAddOns,
  onAssignAddOns,
  onCreateAccount,
  accountConfiguration,
  selectedTemplates,
  addTemplatePacksToAccount,
  addDomainToAccountAction,
  addTemplatesToAccount,
  hasGlobalAccess,
  setSelectedTemplates,
  navigate,
  location,
  notifyConfigPayload,
  prefilled,
  onRemoveName,
  resetAccountConfiguration,
  notifyConfig,
  selectedPacks,
  hasTemplateUsersTabAccess,
  hasTemplatesTabAccess,
  selectedUsers,
  setSelectedUsers,
  reset,
}) => {
  const wizardSteps = useMemo(() => {
    return getCreateSteps(hasTemplatesTabAccess, hasTemplateUsersTabAccess);
  }, [hasTemplatesTabAccess, hasTemplateUsersTabAccess]);

  const onTriggerConfirmLicensingObserver =
    useObservable<(onConfirm: (packs: PackAccountsContextItem[]) => void, templateIds: number[]) => void>();

  const [isAccountConfigValid, setIsAccountConfigValid] = useState<boolean>(false);
  const [isAccountConfigDirty, setIsAccountConfigDirty] = useState<boolean>(false);
  const [contacts, setContacts] = useState<NewContact[]>([]);
  const [editingAddOns, setEditingAddOns] = useState<EditingAddOn[]>([]);
  const [isAccountIdPending, setIsAccountIdPending] = useState<boolean>(false);
  const [parentAccountId, setParentAccountId] = useState<number | null>(null);
  const [areTemplatesConfirmed, setAreTemplatesConfirmed] = useState<boolean>(false);
  const [createdAccountId, setCreatedAccountId] = useState<number>(0);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [packsStep, setPacksStep] = useState({
    showAddPacksModal: false,
    arePacksConfirmed: false,
    addingPacksInProgress: false,
  });

  const [notifyStep, setNotifyStep] = useState({
    isValid: false,
    isLoaded: false,
  });

  const isFinishingRef = useRef(false);

  const templateUsersRef = useRef<TemplateUsersRef>(null);
  const onAddUserObserver = useObservable<(onConfirm: () => void) => void>();
  const onImportUsersObserver = useObservable<(onConfirm: () => void) => void>();
  const onDeleteUsersObserver = useObservable<(onConfirm: () => void, ids: number[]) => void>();

  const resolvePacksConfirmationProgressRef = useRef<(() => void) | undefined>();
  const rejectPacksConfirmationProgressRef = useRef<(() => void) | undefined>();
  const NotifyByEmailControllerRef = useRef<INotifyByEmailController | null>(null);
  const submitAccountConfigurationRef = useRef<any>(null);

  const handleAccountCreatedEvent = useCallback(
    (event: { payload: { id: number } }) => {
      setIsAccountIdPending(false);
      setCreatedAccountId(event.payload.id);
      fetchAddOns(event.payload.id);
    },
    [fetchAddOns],
  );

  const handleCompletePackStep = useCallback(() => {
    fetchAddOns(createdAccountId);
    setPacksStep((prev) => ({
      ...prev,
      addingPacksInProgress: false,
    }));
  }, [fetchAddOns, createdAccountId]);

  useRtn(accountsEventTypes.AccountCreatedSuccess, handleAccountCreatedEvent);
  useRtn(packsRtn, handleCompletePackStep);

  useEffect(() => {
    if (addOns.items) {
      setEditingAddOns(addOns.items.map((addOn: AddOn) => new EditingAddOn(addOn)));
    }
  }, [addOns.items]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => reset, []);

  const handleCancel = () => {
    navigationUtils.goBackOrDefault(location, navigate, RouteNames.linkedAccountsManagement);
  };

  const bindSubmitForm = (submitForm: any) => {
    submitAccountConfigurationRef.current = submitForm;
  };

  const handleAccountCreateFinish = async () => {
    if (isFinishingRef.current || !NotifyByEmailControllerRef.current) {
      return;
    }

    isFinishingRef.current = true;

    await notifyUser();
    resetAccountConfiguration();
    navigate("/accounts/linked");
  };

  const createAccount = async () => {
    setIsAccountIdPending(true);
    onCreateAccount({ account: accountConfiguration, contacts });
  };

  const handleProgressAsync = async (currentIndex: number, nextIndex: number) => {
    const result = [];

    if (currentIndex === wizardSteps.Configure) {
      await submitAccountConfigurationRef.current();
      if (!isAccountConfigValid) {
        result.push("cannot submit invalid form");
      }
    } else if (currentIndex === wizardSteps.Contacts) {
      await createAccount();
    } else if (currentIndex === wizardSteps.AddOn) {
      processAddonsStep();
    } else if (currentIndex === wizardSteps.TemplateUsers) {
      await templateUsersRef.current?.save();
    }

    setActiveStepIndex(nextIndex);
    return result;
  };

  const handleRegressAsync = (currentIndex: number, nextIndex: number): Promise<any> => {
    if (currentIndex === wizardSteps.AddOn) {
      processAddonsStep();
    }

    setActiveStepIndex(nextIndex);
    const notifyTab = hasGlobalAccess ? wizardSteps.Notify : wizardSteps.AddOn;
    if (currentIndex !== notifyTab || !notifyStep.isLoaded) {
      return Promise.resolve();
    }
    return NotifyByEmailControllerRef.current!.saveChanges();
  };

  const canProceed = (currentIndex: number) => {
    if (currentIndex > wizardSteps.Contacts && createdAccountId === 0) {
      return Promise.resolve(false);
    }

    if (currentIndex === wizardSteps.Templates && selectedTemplates.length !== 0) {
      onTriggerConfirmLicensingObserver.notify((packs: PackAccountsContextItem[]) => {
        setAreTemplatesConfirmed(true);
        resolvePacksConfirmationProgressRef.current?.();
        addTemplatePacksToAccount(createdAccountId, packs);
        addTemplatesToAccount(createdAccountId, selectedTemplates, true);
        addDomainToAccountAction([createdAccountId]);
      }, selectedTemplates);

      return new Promise((resolve) => {
        resolvePacksConfirmationProgressRef.current = () => resolve(true);
      });
    }

    if (currentIndex === wizardSteps.Packs && selectedPacks.length !== 0) {
      setPacksStep((prev) => ({
        ...prev,
        showAddPacksModal: true,
      }));

      return new Promise((resolve) => {
        resolvePacksConfirmationProgressRef.current = () => resolve(true);
        rejectPacksConfirmationProgressRef.current = () => resolve(false);
      });
    }

    return Promise.resolve(true);
  };

  const handleConfirmAddPacksModal = () => {
    setPacksStep({
      showAddPacksModal: false,
      arePacksConfirmed: true,
      addingPacksInProgress: true,
    });
    resolvePacksConfirmationProgressRef.current?.();
  };

  const handleCancelAddPacksModal = () => {
    setPacksStep((prev) => ({
      ...prev,
      showAddPacksModal: false,
    }));
    rejectPacksConfirmationProgressRef.current?.();
    setSelectedPacks([]);
  };

  const canRecede = (_currentIndex: number, nextIndex: number) => {
    const canRecede =
      nextIndex > wizardSteps.Contacts &&
      (nextIndex !== wizardSteps.Templates || (!areTemplatesConfirmed && !packsStep.arePacksConfirmed)) &&
      (nextIndex !== wizardSteps.Packs || !packsStep.arePacksConfirmed);

    return (createdAccountId === 0 || canRecede) && !isAccountIdPending;
  };

  const assignAddOns = async (addOns: Array<SavingAddOn>) => {
    onAssignAddOns({ addOns, accountId: createdAccountId });
    updateAddOnsState(addOns);
  };

  const updateAddOnsState = (addons: Array<SavingAddOn>) => {
    const updatedAddOns = editingAddOns.map((addOn) => clone(addOn));

    updatedAddOns.forEach((addOn) => {
      const savedAddOn = addons.find((a) => a.id === addOn.id);
      if (savedAddOn) {
        addOn.origin.isEnabled = true;
        addOn.origin.expirationDate = savedAddOn.expirationDate;
      } else {
        addOn.origin.isEnabled = false;
        addOn.origin.expirationDate = null;
      }
    });

    setEditingAddOns(updatedAddOns);
  };

  const processAddonsStep = () => {
    if ((hasGlobalAccess || !!parentAccountId) && editingAddOns.some((addOn) => addOn.isChanged)) {
      const enabledAddOns = editingAddOns
        .filter((addOn) => addOn.isEnabled)
        .map((addOn) => ({ id: addOn.id, expirationDate: addOn.expirationDate }));
      assignAddOns(enabledAddOns);
    }
  };

  const shouldPreventTransition = useMemo(() => {
    return (
      (createdAccountId === 0 && (isAccountConfigDirty || contacts.length > 0 || !isEmpty(accountConfiguration))) ||
      selectedTemplates.length > 0 ||
      selectedPacks.length > 0 ||
      editingAddOns.some((addOn) => addOn.isChanged)
    );
  }, [
    contacts.length,
    createdAccountId,
    editingAddOns,
    isAccountConfigDirty,
    accountConfiguration,
    selectedTemplates.length,
    selectedPacks.length,
  ]);

  const notifyUser = async () => {
    const notificationSettings = await NotifyByEmailControllerRef.current!.getSettings(
      notifyConfigPayload.shouldNotify,
      notifyConfigPayload.communicationChannel,
    );

    if (!notificationSettings.suppressNotification) {
      const mainContact = contacts.find((x) => x.isMain);
      await dataService.notifyUserByEmail(
        mainContact?.email,
        createdAccountId,
        TemplateTypes.AccountCreated,
        notificationSettings,
      );
    }
  };

  const handleNotifyStepLoaded = useCallback((controller: INotifyByEmailController) => {
    NotifyByEmailControllerRef.current = controller;
    setNotifyStep((prev) => ({
      ...prev,
      isLoaded: true,
    }));
  }, []);

  const handleNotifyStepValidChange = useCallback((isValid: boolean) => {
    setNotifyStep((prev) => ({
      ...prev,
      isValid,
    }));
  }, []);

  const handleSelectedTemplatesPacksModalClose = () => {
    setSelectedTemplates([]);
  };

  const handleParentAccountChange = (newParentAccountId: number | null) => {
    if (newParentAccountId !== parentAccountId) {
      setParentAccountId(newParentAccountId);
    }
  };

  const renderAddUserButton = (tooltipPosition?: string) => {
    return (
      <AddTemplateUserButton
        onCreateUser={() => onAddUserObserver.notify(noop)}
        onImportUsers={() => onImportUsersObserver.notify(noop)}
        tooltipPosition={tooltipPosition}
      />
    );
  };

  const renderDeleteUserButton = () => {
    return (
      <DeleteLinkButton
        onClick={() => {
          onDeleteUsersObserver.notify(() => {
            setSelectedUsers([]);
          }, selectedUsers);
        }}
      />
    );
  };

  const renderCustomHeader = () => {
    if (activeStepIndex === wizardSteps.TemplateUsers) {
      return selectedUsers.length > 0 ? renderDeleteUserButton() : renderAddUserButton();
    }

    return null;
  };

  return (
    <>
      <Wizard
        id="create-account-wizard"
        className="create-account"
        title="Create Account"
        onProgressAsync={handleProgressAsync}
        onCancel={handleCancel}
        onFinishAsync={handleAccountCreateFinish}
        onRegressAsync={handleRegressAsync}
        finishButtonLabel="Finish"
        isSaveInProgress={false}
        canProceedAsync={canProceed}
        canRecede={canRecede}
        isPreviousDisabled={!canRecede(0, activeStepIndex - 1)}
        renderCustomHeader={renderCustomHeader}
      >
        <Section label="Configuration" isLocked={!isAccountConfigValid} required>
          <CreateAccountConfiguration
            onParentAccountChange={handleParentAccountChange}
            prefilled={prefilled}
            onRemoveName={onRemoveName}
            bindSubmitForm={bindSubmitForm}
            onIsValidChange={setIsAccountConfigValid}
            onDirtyChanged={setIsAccountConfigDirty}
          />
        </Section>

        <Section label="Contacts" isLocked={contacts.length === 0} required>
          <Contacts contacts={contacts} onContactsChanged={setContacts} />
        </Section>

        {hasTemplatesTabAccess && (
          <Section label="Templates" isLocked={createdAccountId === 0}>
            <CreateAccountTemplates accountId={createdAccountId} />
          </Section>
        )}

        {hasTemplateUsersTabAccess && (
          <Section label="Template Users">
            <CreateAccountTemplateUsers
              ref={templateUsersRef}
              accountId={createdAccountId}
              onAddUserModalObserver={onAddUserObserver}
              onDeleteUsersObserver={onDeleteUsersObserver}
              onImportUsersModalObserver={onImportUsersObserver}
              renderAddUsersButton={renderAddUserButton}
            />
          </Section>
        )}

        {(hasGlobalAccess || !!parentAccountId) && (
          <Section label="Packs" isLocked={createdAccountId === 0}>
            <CreateAccountPacksList
              createdAccountId={createdAccountId}
              onConfirm={handleConfirmAddPacksModal}
              onCancel={handleCancelAddPacksModal}
              isModalOpen={packsStep.showAddPacksModal}
            />
          </Section>
        )}

        {(hasGlobalAccess || !!parentAccountId) && (
          <Section label="Add-ons" isLocked={addOns.isLoading}>
            <Restricted
              permissions={[RolePermissions.AddonsManage]}
              permissionPredicate={(userPermissionsList, permissionsList) =>
                difference(permissionsList, userPermissionsList).length === 0
              }
              placeholder={<AccessRestrictedMessage />}
            >
              <RestrictedResource isAuthorized={addOns.isAccessAuthorized}>
                <AddOnsConfiguration
                  addOns={editingAddOns}
                  onMount={() => createdAccountId !== 0 && fetchAddOns(createdAccountId)}
                  onChangeAddons={setEditingAddOns}
                  isLoading={addOns.isLoading || packsStep.addingPacksInProgress}
                  isReadOnly={false}
                />
              </RestrictedResource>
            </Restricted>
          </Section>
        )}

        <Section label="Notify" isLocked={notifyConfigPayload.shouldNotify && !notifyStep.isValid}>
          <NotifyWizardStep
            templateType={TemplateTypes.AccountCreated}
            onLoaded={handleNotifyStepLoaded}
            onValidChange={handleNotifyStepValidChange}
            shouldNotify={notifyConfigPayload.shouldNotify}
            communicationChannel={notifyConfigPayload.communicationChannel}
            hideCustomSender
            renderSwitch={(switchProps) => (
              <NotifyStepSwitch
                config={notifyConfig}
                onNotifyConfigChange={notifyConfigPayload.setNotifyConfig}
                switchProps={switchProps}
              />
            )}
          />
        </Section>
      </Wizard>
      <PreventTransitionPrompt
        when={shouldPreventTransition}
        title="Exit Without Saving?"
        message="Are you sure you want to exit without saving this account? All information entered will be lost."
      />
      <ConfirmTemplatesPacksModal
        onTriggerModalObserver={onTriggerConfirmLicensingObserver}
        onClose={handleSelectedTemplatesPacksModalClose}
      />
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { creation, addOns, availableTemplates } = state.accounts;
  const { userProfile } = state;

  return {
    accountConfiguration: creation.configuration,
    addOns,
    hasGlobalAccess: userProfile.hasGlobalAccess,
    selectedTemplates: availableTemplates.overview.selected,
    selectedPacks: getSelectedPacks(state),
    hasTemplateUsersTabAccess:
      selectHasTemplateUsersTabAccess(state) && isAccountTypeSelector(state, AccountTypeEnum.SoftwareVendor),
    hasTemplatesTabAccess: selectHasTemplatesTabAccess(state),
    selectedUsers: state.accounts.templateUsers.selected,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    fetchAddOns: bindAction(fetchAddOns, dispatch),
    addTemplatePacksToAccount: bindAction(saveTemplatesPacks, dispatch),
    addTemplatesToAccount: bindAction(addTemplatesThunk, dispatch),
    setSelectedTemplates: bindAction(setSelected, dispatch),
    onCreateAccount: bindAction(createAccountThunk, dispatch),
    onAssignAddOns: bindAction(assignAddOnsAction, dispatch),
    resetAccountConfiguration: bindAction(resetAccountConfigurationAction, dispatch),
    setSelectedPacks: bindAction(setSelectedPacks, dispatch),
    addDomainToAccountAction: bindAction(addDomainToAccount, dispatch),
    setSelectedUsers: bindAction(setSelectedUsersAction, dispatch),
    reset: () => {
      dispatch(resetAccountConfigurationAction());
      dispatch(resetAddOnsAction());
      dispatch(resetCustomers());
      dispatch(resetAvailableDomains());
      dispatch(resetTemplateUsers());
    },
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(withNotifyConfig(withRouter(CreateAccount), initialNotifyConfigDefault));

export default ConnectedComponent;
